import dynamic from 'next/dynamic';
import { ArrowRight } from '@phosphor-icons/react';
import { Box, Flex, Text, Heading } from '@chakra-ui/react';
import { useTranslation } from '../lib/hooks.context';
import { MhyChakraProps, Person } from '../types';
import BasicTeaser, { TeaserHeader } from './BasicTeaser';
import ButtonLink from './ButtonLink';
import Image from './Image';
import Link from './Link';
import { formatPhoneNumber } from '../lib/utils';

const ArrowLink = dynamic(() => import('./ArrowLink'));

const PersonTeaser = ({
  firstname,
  surname,
  office: [primaryOffice],
  showOfficeDetails,
  roles,
  phone,
  email,
  extra_link,
  extra_text,
  territory,
  municipality,
  image_src,
  image_src_md,
  id,
  boardMember = false,
  blog_id,
  site,
  ...rest
}: Person & Omit<MhyChakraProps, 'variant'> & { boardMember?: boolean }) => {
  const t = useTranslation();
  const imageContent = image_src_md ? (
    <Image
      src={image_src_md}
      useUnoptimizedImage
      height="120"
      width="96"
      alt={`${firstname} ${surname}`}
    />
  ) : null;

  const content = boardMember ? (
    <>
      <Box>
        <Heading variant="h4" as="h2" mb=".125em">
          {`${firstname} ${surname}`}
        </Heading>
        {roles?.length ? <Text textStyle="sm">{roles}</Text> : null}
        {municipality ? <Text textStyle="sm">{municipality}</Text> : null}
      </Box>
      <Flex flexDirection="column" gridRowGap="1" marginTop="auto">
        {phone && (
          <Link href={`tel:${phone}`} target="_blank">
            {formatPhoneNumber(phone)}
          </Link>
        )}
        {email && (
          <Link href={`mailto:${email}`} target="_blank">
            {email}
          </Link>
        )}
      </Flex>
    </>
  ) : (
    <>
      <Box>
        <Heading variant="h4" as="h2" mb=".125em">
          {`${firstname} ${surname}`}
        </Heading>
        <Flex flexDirection="column" gridRowGap="2">
          {roles?.length ? <Text textStyle="sm">{roles}</Text> : null}
          {extra_text?.length ? <Text textStyle="sm">{extra_text}</Text> : null}
          {extra_link ? (
            <ArrowLink href={extra_link.url} target={extra_link.target}>
              {extra_link.title}
            </ArrowLink>
          ) : null}
        </Flex>
      </Box>
      {territory?.length ? (
        <Box>
          <Text textStyle="sm" color="secondaryColor" mb="1">
            {t('area_of_operation')}
          </Text>
          <Text textStyle="sm">{territory.join(', ')}</Text>
        </Box>
      ) : null}
      <Flex flexDirection="column" gridGap={2}>
        {phone && (
          <ButtonLink
            link={{
              url: `tel:${phone}`,
              title: t('call_phone', { phone: formatPhoneNumber(phone) }),
              target: '_blank',
            }}
            variant="solidGreen"
          />
        )}
        {email && (
          <ButtonLink
            link={{ url: `mailto: ${email}`, title: email, target: '_blank' }}
            variant="outlineGreen"
          />
        )}
      </Flex>
    </>
  );

  const showTeaserHeader = Boolean((primaryOffice || site) && !boardMember);

  const officeId = typeof primaryOffice !== 'string' ? primaryOffice?.id : null;

  return (
    <BasicTeaser maxWidth="500px" {...rest} hasTeaserHeader={showTeaserHeader}>
      {showTeaserHeader && (
        <TeaserHeader>
          {!blog_id || typeof primaryOffice === 'string' ? (
            // This is to support old version of Person object existing in
            // cache and transients in db. The old Person object lacks
            // blog_id and primaryOffice is a string instead of object.
            <Text textStyle="sm" fontWeight="500" lineHeight="1.75">
              {`${site ? `MHY ${site}${primaryOffice ? ' - ' : ''}` : ''}${
                primaryOffice || ''
              }`}
            </Text>
          ) : (
            <Text
              cursor="pointer"
              textStyle="sm"
              fontWeight="500"
              lineHeight="1.75"
              width="100%"
              position="relative"
              paddingRight={6}
              onClick={
                showOfficeDetails
                  ? () => showOfficeDetails(blog_id, officeId)
                  : undefined
              }
            >
              {`${site ? `MHY ${site}${primaryOffice ? ' - ' : ''}` : ''}${
                primaryOffice ? primaryOffice?.name : ''
              }`}
              <Box as="span" position="absolute" top={1} right="0">
                <ArrowRight size="16" />
              </Box>
            </Text>
          )}
        </TeaserHeader>
      )}
      {imageContent}
      {content}
    </BasicTeaser>
  );
};

export default PersonTeaser;
